/**
 * Adherencia (§5.5).
 *
 * Ojo con §10: la racha se calcula, pero **una racha rota no es un fracaso**. Nada de esto
 * habilita notificaciones culpabilizantes ni penalizaciones; es información para decidir si
 * la rutina es demasiado larga, no un castigo.
 */

import { aTimestamp, diffDias } from '../dates.ts';
import type { Fecha, Plan, Sesion, SerieContexto } from '../types.ts';

export interface Adherencia {
  completadas: number;
  planificadas: number;
  omitidas: number;
  /** completadas / (completadas + omitidas). null si todavía no hubo ninguna. */
  tasa: number | null;
  rachaActual: number;
  rachaMaxima: number;
}

/**
 * Racha = sesiones completadas consecutivas. La rompe una sesión marcada como omitida, no
 * el paso del tiempo: descansar el domingo porque el gimnasio cierra no rompe nada.
 */
export function adherencia(sesiones: Sesion[], hasta?: Fecha): Adherencia {
  const relevantes = sesiones
    .filter((s) => (hasta ? s.fecha <= hasta : true))
    .filter((s) => s.estado === 'completada' || s.estado === 'omitida')
    .sort((a, b) => aTimestamp(a.fecha) - aTimestamp(b.fecha));

  const completadas = relevantes.filter((s) => s.estado === 'completada').length;
  const omitidas = relevantes.filter((s) => s.estado === 'omitida').length;

  let rachaActual = 0;
  let rachaMaxima = 0;
  for (const s of relevantes) {
    if (s.estado === 'completada') {
      rachaActual++;
      rachaMaxima = Math.max(rachaMaxima, rachaActual);
    } else {
      rachaActual = 0;
    }
  }

  const planificadas = sesiones.filter((s) => (hasta ? s.fecha <= hasta : true)).length;

  return {
    completadas,
    planificadas,
    omitidas,
    tasa: completadas + omitidas > 0 ? completadas / (completadas + omitidas) : null,
    rachaActual,
    rachaMaxima,
  };
}

export interface EstadoPlan {
  sesionesTotales: number;
  usadas: number;
  restantes: number;
  diasHastaVencimiento: number;
  vencido: boolean;
}

export function estadoPlan(plan: Plan, sesiones: Sesion[], hoy: Fecha): EstadoPlan {
  const usadas = sesiones.filter((s) => s.plan_id === plan.id && s.estado === 'completada').length;
  const diasHastaVencimiento = diffDias(hoy, plan.fecha_vencimiento);
  return {
    sesionesTotales: plan.sesiones_totales,
    usadas,
    restantes: Math.max(0, plan.sesiones_totales - usadas),
    diasHastaVencimiento,
    vencido: diasHastaVencimiento < 0,
  };
}

// ---------------------------------------------------------------------------
// Tasa de finalización dentro de la sesión (§5.5)
// ---------------------------------------------------------------------------

export interface FinalizacionSesion {
  sesion_id: string;
  fecha: Fecha;
  ejerciciosPlanificados: number;
  ejerciciosHechos: number;
  tasa: number | null;
}

/**
 * §5.5 — detecta rutinas que se abandonan a la mitad, que es señal de que la rutina es
 * demasiado larga, no de que el usuario sea flojo.
 *
 * `ejerciciosPorDia` mapea `rutina_dia_id` a la cantidad de ejercicios planificados.
 */
export function finalizacionPorSesion(
  sesiones: Sesion[],
  ctxs: SerieContexto[],
  ejerciciosPorDia: Map<string, number>,
): FinalizacionSesion[] {
  const hechosPorSesion = new Map<string, Set<string>>();
  for (const c of ctxs) {
    if (!c.serie.completada) continue;
    const set = hechosPorSesion.get(c.sesion_id) ?? new Set<string>();
    set.add(c.serie.ejercicio_id);
    hechosPorSesion.set(c.sesion_id, set);
  }

  return sesiones
    .filter((s) => s.estado === 'completada' || s.estado === 'en_curso')
    .map((s) => {
      const planificados = s.rutina_dia_id ? (ejerciciosPorDia.get(s.rutina_dia_id) ?? 0) : 0;
      const hechos = hechosPorSesion.get(s.id)?.size ?? 0;
      return {
        sesion_id: s.id,
        fecha: s.fecha,
        ejerciciosPlanificados: planificados,
        ejerciciosHechos: hechos,
        tasa: planificados > 0 ? Math.min(1, hechos / planificados) : null,
      };
    })
    .sort((a, b) => aTimestamp(a.fecha) - aTimestamp(b.fecha));
}

export function tasaFinalizacionPromedio(items: FinalizacionSesion[]): number | null {
  const conTasa = items.filter((i) => i.tasa !== null);
  if (conTasa.length === 0) return null;
  return conTasa.reduce((a, i) => a + i.tasa!, 0) / conTasa.length;
}
